import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin, ArrowRight, Laptop } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const LadyBirdDeedTampa = () => {
  return (
    <>
      <Helmet>
        <title>Lady Bird Deed Attorney Tampa FL — Statewide Florida Service</title>
        <meta name="description" content="Tampa Lady Bird Deed attorney. Avoid probate, protect your Hillsborough County home. Remote service available statewide. Call 941-206-2223 for free consultation." />
        <link rel="canonical" href="https://thelawyerlady.com/lady-bird-deed-tampa-fl" />
        <meta property="og:title" content="Lady Bird Deed Attorney Tampa FL — Statewide Florida Service" />
        <meta property="og:description" content="Tampa Lady Bird Deed attorney. Avoid probate, protect your Hillsborough County home. Remote service available statewide." />
        <meta property="og:url" content="https://thelawyerlady.com/lady-bird-deed-tampa-fl" />
        <meta property="og:type" content="website" />
        
        <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thelawyerlady.com" },
            { "@type": "ListItem", "position": 2, "name": "Lady Bird Deed Florida", "item": "https://thelawyerlady.com/lady-bird-deed-florida" },
            { "@type": "ListItem", "position": 3, "name": "Tampa", "item": "https://thelawyerlady.com/lady-bird-deed-tampa-fl" }
          ]
        })}
        </script>
        
        <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Attorney",
          "name": "Season A. Spanski",
          "alternateName": "Spanski Law, PLLC",
          "url": "https://thelawyerlady.com",
          "telephone": "+1-941-206-2223",
          "email": "contact@thelawyerlady.com",
          "address": { "@type": "PostalAddress", "addressLocality": "Venice", "addressRegion": "FL", "addressCountry": "US" },
          "areaServed": [{ "@type": "City", "name": "Tampa" }, { "@type": "County", "name": "Hillsborough County" }, { "@type": "State", "name": "Florida" }],
          "priceRange": "$800-$1000"
        })}
        </script>
      </Helmet>

      <div className="min-h-screen bg-[#0a0a0a]">
        {/* Breadcrumb */}
        <div className="max-w-4xl mx-auto px-6 pt-24 pb-4">
          <nav className="flex items-center gap-2 text-sm text-[#faf9f7]/60">
            <Link to="/" className="hover:text-[#c9a87c] transition-colors">Home</Link>
            <span>/</span>
            <Link to="/lady-bird-deed-florida" className="hover:text-[#c9a87c] transition-colors">Lady Bird Deed Florida</Link>
            <span>/</span>
            <span className="text-[#c9a87c]">Tampa</span>
          </nav>
        </div>

        {/* Hero Section */}
        <section className="max-w-4xl mx-auto px-6 py-12">
          <h1 className="font-serif text-4xl md:text-5xl text-[#faf9f7] mb-6">
            Lady Bird Deed Attorney Serving Tampa FL — Statewide Florida Service
          </h1>
          <p className="font-sans text-xl text-[#faf9f7]/70 mb-8 leading-relaxed">
            I'm Season Spanski, and I help Tampa Bay area homeowners protect their property with Lady Bird Deeds. My office is about an hour south in Venice, and I serve clients throughout Florida remotely.
          </p>
          
          <div className="flex flex-wrap gap-4 mb-8">
            <a href="tel:941-206-2223" className="btn-elegant inline-flex items-center gap-2 px-6 py-3 bg-[#c9a87c] text-[#0a0a0a] font-sans font-semibold rounded-sm hover:bg-[#d4b896]">
              <Phone className="w-5 h-5" />
              Call 941-206-2223
            </a>
            <Link to="/#contact" className="btn-elegant inline-flex items-center gap-2 px-6 py-3 border border-[#c9a87c] text-[#c9a87c] font-sans font-medium rounded-sm hover:bg-[#c9a87c]/10">
              Free Consultation
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>

          <div className="flex flex-wrap gap-6 text-sm text-[#faf9f7]/60">
            <div className="flex items-center gap-2">
              <Laptop className="w-4 h-4 text-[#c9a87c]" />
              <span>Remote Service Available</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-[#c9a87c]" />
              <span>Serving Hillsborough, Pinellas & Pasco Counties</span>
            </div>
          </div>
        </section>

        {/* Main Content */}
        <article className="max-w-4xl mx-auto px-6 py-12">
          <div className="prose prose-lg prose-invert max-w-none">
            
            <h2 className="font-serif text-3xl text-[#faf9f7] mb-6">Lady Bird Deeds for Tampa Bay Homeowners</h2>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              The Tampa Bay area is home to millions of Florida homeowners, and many of them don't realize there's a simple way to pass their property to family without going through probate. A Lady Bird Deed makes it possible.
            </p>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              With a Lady Bird Deed, you name beneficiaries for your home, but you keep full control during your lifetime. Sell it, refinance it, rent it out — it's still your property to do with as you please. When you pass away, the property transfers automatically to your named beneficiaries. No probate, no court, no delays.
            </p>

            <h2 className="font-serif text-3xl text-[#faf9f7] mb-6 mt-12">Serving Tampa Remotely — No Travel Required</h2>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              While my office is in Venice, I regularly work with Tampa clients. Thanks to Florida's remote online notarization laws, you can get a Lady Bird Deed done without leaving your home:
            </p>
            <ul className="list-disc pl-6 text-[#faf9f7]/80 space-y-2 mb-6">
              <li>We consult by phone or video call</li>
              <li>I prepare your deed and send it for review via email</li>
              <li>You sign through a secure video notarization session</li>
              <li>I record the deed with Hillsborough County (or your property's county)</li>
            </ul>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              The whole process typically takes 1-2 weeks, and you never have to drive anywhere.
            </p>

            <h2 className="font-serif text-3xl text-[#faf9f7] mb-6 mt-12">Cost and Timeline</h2>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              My flat fee for a Lady Bird Deed is $800-$1,000. This covers the consultation, deed preparation, review, and assistance with signing. County recording fees are additional — usually $30-50 in Hillsborough County.
            </p>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              Most Tampa clients have their deed recorded within two weeks of our first conversation. If you need it faster, let me know and I'll do what I can.
            </p>

            <h2 className="font-serif text-3xl text-[#faf9f7] mb-6 mt-12">Tampa Bay Area Coverage</h2>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              I serve homeowners throughout the Tampa Bay region, including Hillsborough County, Pinellas County, Pasco County, and Manatee County. Whether you're in Tampa, St. Petersburg, Clearwater, or Brandon, I can help.
            </p>
            <p className="text-[#faf9f7]/80 mb-6 leading-relaxed">
              Learn more about my <Link to="/lady-bird-deed-florida" className="text-[#c9a87c] hover:underline">statewide services</Link>, or see how I help homeowners in <Link to="/lady-bird-deed-sarasota" className="text-[#c9a87c] hover:underline">Sarasota</Link>, <Link to="/lady-bird-deed-orlando-fl" className="text-[#c9a87c] hover:underline">Orlando</Link>, and <Link to="/lady-bird-deed-fort-lauderdale" className="text-[#c9a87c] hover:underline">Fort Lauderdale</Link>.
            </p>

          </div>
        </article>

        {/* CTA Section */}
        <section className="max-w-4xl mx-auto px-6 py-16">
          <div className="bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border border-[#c9a87c]/20 rounded-lg p-8 md:p-12 text-center">
            <h2 className="font-serif text-3xl text-[#faf9f7] mb-4">Ready to Protect Your Tampa Home?</h2>
            <p className="text-[#faf9f7]/70 mb-8 max-w-2xl mx-auto">
              Call 941-206-2223 or schedule a free consultation online. I'll answer your questions and explain whether a Lady Bird Deed is right for your situation.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:941-206-2223" className="btn-elegant inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#c9a87c] text-[#0a0a0a] font-sans font-semibold rounded-sm hover:bg-[#d4b896]">
                <Phone className="w-5 h-5" />
                Call 941-206-2223
              </a>
              <Link to="/#contact" className="btn-elegant inline-flex items-center justify-center gap-2 px-8 py-4 border border-[#c9a87c] text-[#c9a87c] font-sans font-medium rounded-sm hover:bg-[#c9a87c]/10">
                Schedule Online
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default LadyBirdDeedTampa;
